/**
 * @file indexnow-view.ts
 * @description Подписи карточки «Сообщать поисковикам об изменениях»
 * (IndexNow) во вкладке «Сайт → SEO → Основное» (phase19.16, партия
 * «SEO-техника», v0.19.65; SEO7, SEO8; макет uiux-seo-tech.md §3.2, таблица
 * состояний; решения оркестратора §3а п. 2, 12, 19).
 *
 * Чистые функции — их проверяют тесты, карточка лишь рисует:
 *  - итог последней отправки по коду ответа → тон и фраза (реестр по
 *    `IndexNowResultKind` и отдельным кодам 202/403/422/429);
 *  - почему сейчас не отправляем (`blockedBy`) → плашка;
 *  - короткий ключ «3f9a1c…c21e» и строка адреса «основной — отправляем».
 */

import type { IndexNowBlockReason, IndexNowKeyDto, IndexNowLastDto } from "@chaiberi/shared";

import { pluralRu } from "../plural";
import type { StatusTone } from "../shop/status-meta";

/** Итог отправки словами владельца: короткая пилюля и, если нужно, пояснение. */
export interface IndexNowResultView {
  tone: StatusTone;
  /** Пилюля: «принято», «не принято», «повторим». */
  pill: string;
  /** Что случилось и что делать (плашкой под строкой); нет — пилюли достаточно. */
  detail?: string;
}

/** Ответы с особой фразой (uiux §3.2, таблица состояний). */
const STATUS_TEXT: Readonly<Record<number, IndexNowResultView>> = {
  200: { tone: "success", pill: "принято" },
  202: { tone: "progress", pill: "принято, поисковик проверяет ключ" },
  403: { tone: "error", pill: "не принято", detail: "Поисковик не нашёл файл ключа на сайте. Откройте файл ключа — если он не открывается, напишите в поддержку платформы." },
  422: { tone: "error", pill: "не принято", detail: "Поисковик отклонил адреса: они не относятся к этому сайту. Напишите в поддержку платформы." },
  429: { tone: "attention", pill: "повторим", detail: "Слишком часто — повторим позже сами." },
};

/** Итог по виду, когда своей фразы у кода нет. */
const KIND_TEXT: Readonly<Record<IndexNowLastDto["kind"], IndexNowResultView>> = {
  accepted: { tone: "success", pill: "принято" },
  rejected: { tone: "error", pill: "не принято", detail: "Поисковик отклонил запрос. Напишите в поддержку платформы." },
  retry: { tone: "attention", pill: "повторим", detail: "Сервис IndexNow не ответил. Повторим при следующем изменении; поисковики всё равно дойдут обходом." },
  mock: { tone: "neutral", pill: "тестовая среда", detail: "Адреса записаны в журнал сервера, поисковикам не отправлены." },
};

export function indexNowResultView(last: Pick<IndexNowLastDto, "status" | "kind">): IndexNowResultView {
  const byStatus = typeof last.status === "number" ? STATUS_TEXT[last.status] : undefined;
  return byStatus ?? KIND_TEXT[last.kind];
}

/** «3 адреса». */
export const urlsCountText = (n: number): string => `${n} ${pluralRu(n, ["адрес", "адреса", "адресов"])}`;

/** Сколько адресов последней отправки показывать (сервер хранит до 10, решение §3а п. 12). */
export const INDEXNOW_SHOWN_URLS = 10;

/** «и ещё N» сверх показанных адресов; нет — null. */
export function moreUrlsText(last: Pick<IndexNowLastDto, "count" | "urls">): string | null {
  const rest = last.count - Math.min(last.urls.length, INDEXNOW_SHOWN_URLS);
  return rest > 0 ? `и ещё ${urlsCountText(rest)}` : null;
}

/** Почему не отправляем при включённом переключателе — плашка «внимание». */
export const INDEXNOW_BLOCK_TEXT: Readonly<Record<IndexNowBlockReason, string>> = {
  env: "Это тестовый адрес — отправка выключена платформой.",
  site_noindex: "Сейчас не отправляем: сайт закрыт от поисковиков (см. «Индексация»).",
  site_closed: "Сейчас не отправляем: сайт закрыт на работы — отправим, когда откроете. Изменения за время паузы не копятся.",
  no_primary: "У магазина пока нет своего адреса — отправка начнётся после подключения домена.",
};

/** Ключ коротко: первые 6 и последние 4 знака. */
export function shortKey(key: string): string {
  return key.length <= 12 ? key : `${key.slice(0, 6)}…${key.slice(-4)}`;
}

/** «основной — отправляем» / «дополнительный — переадресует на основной, не отправляем». */
export function keyRoleText(key: Pick<IndexNowKeyDto, "isPrimary">): string {
  return key.isPrimary ? "основной — отправляем" : "дополнительный — переадресует на основной, не отправляем";
}

export const INDEXNOW_ON_HINT =
  "Когда вы публикуете, скрываете или меняете адрес страницы, товара, категории или статьи, Яндекс и Bing узнают об этом за минуты (IndexNow). Google так не умеет — он читает карту сайта.";

export const INDEXNOW_OFF_HINT = "Выключено — Яндекс и Bing узнают об изменениях при обычном обходе: от дней до недель.";

export const INDEXNOW_NEVER_SENT = "Пока не отправляли. Первая отправка — после публикации или смены адреса страницы, товара, категории или статьи.";

export const INDEXNOW_FOOTNOTE =
  "Не отправляем, пока сайт закрыт от поиска, на работы или не запущен, и для страниц «Не показывать в поиске». Подтверждать ключ в Вебмастере не нужно: файл ключа на сайте ставит платформа. Google IndexNow не поддерживает.";
